import React, { Suspense, useMemo, useEffect } from 'react';
import { Environment, useVideoTexture } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

function VideoBackground({ url }) {
  const texture = useVideoTexture(url, { loop: true, muted: true });
  const { viewport } = useThree();

  useEffect(() => {
    if (texture && texture.image) {
      texture.image.play?.();
    }
  }, [texture]);

  return (
    <mesh scale={[viewport.width * 2, viewport.height * 2, 1]} position={[0, 0, -5]}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  );
}

// Validation helpers
function hasValidExtension(url, extensions) {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return extensions.some(ext => lower.endsWith(ext));
}

export function validateHdrUrl(url) {
  return hasValidExtension(url, ['.hdr', '.exr']);
}

export function validateImageUrl(url) {
  return hasValidExtension(url, ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.tiff']);
}

export function validateVideoUrl(url) {
  return hasValidExtension(url, ['.mp4', '.webm', '.ogg', '.mov']);
}

export default function SceneBackground({ config }) {
  const { type, hdrUrl, hdrRotation, hdrIntensity, imageUrl, videoUrl, color } = config || {};

  // Pre-load image texture unconditionally to satisfy hooks rules
  const imageTexture = useMemo(() => {
    if (type === 'image' && validateImageUrl(imageUrl)) {
      const tex = new THREE.TextureLoader().load(imageUrl);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    }
    return null;
  }, [type, imageUrl]);

  // Solid color background
  if (type === 'color') {
    return <color attach="background" args={[color || '#0f172a']} />;
  }

  // HDR environment
  if (type === 'hdr' && validateHdrUrl(hdrUrl)) {
    const rotationY = ((hdrRotation ?? 0) * Math.PI) / 180;
    return (
      <Suspense fallback={null}>
        <Environment
          background={true}
          files={hdrUrl}
          backgroundRotation={[0, rotationY, 0]}
          backgroundIntensity={hdrIntensity ?? 1}
        />
      </Suspense>
    );
  }

  // Image background
  if (type === 'image' && validateImageUrl(imageUrl) && imageTexture) {
    return (
      <mesh scale={[20, 20, 1]} position={[0, 0, -5]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={imageTexture} toneMapped={false} />
      </mesh>
    );
  }

  // Video background
  if (type === 'video' && validateVideoUrl(videoUrl)) {
    return (
      <Suspense fallback={null}>
        <VideoBackground url={videoUrl} />
      </Suspense>
    );
  }

  // Default: solid color
  return <color attach="background" args={['#0f172a']} />;
}
